import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BASEURL_CLIENT_BACKEND || 'http://localhost:4000';

class BeneficiarySearchService {
    constructor() {
        this.baseURL = `${API_BASE_URL}/BeneficiaryProfile`;
        this.clientURL = `${API_BASE_URL}/Client`;

        // Configuration axios avec intercepteurs
        axios.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('token');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                }
                return Promise.reject(error);
            }
        );
    }

    /**
     * Recherche des bénéficiaires par IDs
     */
    async searchByBeneficiaryIds(beneficiaryIds) {
        try {
            const results = [];
            for (const id of beneficiaryIds) {
                try {
                    const response = await axios.get(`${this.baseURL}/${id}`);
                    if (response.data) {
                        results.push({
                            id: id,
                            name: response.data.lastName || response.data.nom || `BENEFICIAIRE_${id}`,
                            firstName: response.data.firstName || response.data.prenom || 'Prénom',
                            policeNumber: response.data.policeNumber || response.data.numeroPolice || 'N/A',
                            familyId: response.data.familyId || response.data.numeroFamille || 'N/A',
                            numeroAssuré: response.data.numeroAssuré || response.data.numben || id,
                            isValid: true,
                            fullData: response.data
                        });
                    }
                } catch (error) {
                    console.warn(`Bénéficiaire ${id} non trouvé:`, error.message);
                    results.push({
                        id: id,
                        name: `BENEFICIAIRE_${id}`,
                        firstName: 'Non trouvé',
                        policeNumber: 'N/A',
                        familyId: 'N/A',
                        numeroAssuré: id,
                        isValid: false,
                        error: 'Bénéficiaire non trouvé'
                    });
                }
            }
            return results;
        } catch (error) {
            console.error('Erreur lors de la recherche par IDs:', error);
            throw error;
        }
    }

    /**
     * Recherche des bénéficiaires par numéro de police
     */
    async searchByPoliceNumber(policeNumber) {
        try {
            // Utiliser l'endpoint de liste avec filtre sur la police
            const response = await axios.get(`${this.baseURL}/list`, {
                params: {
                    police: policeNumber,
                    page: 1,
                    pageSize: 100
                }
            });

            if (response.data && response.data.data) {
                return response.data.data.map(beneficiary => ({
                    id: beneficiary.id || beneficiary.idClient,
                    name: beneficiary.lastName || beneficiary.nom || 'Nom non disponible',
                    firstName: beneficiary.firstName || beneficiary.prenom || 'Prénom non disponible',
                    policeNumber: beneficiary.policeNumber || beneficiary.numeroPolice || policeNumber,
                    familyId: beneficiary.familyId || beneficiary.numeroFamille || 'N/A',
                    numeroAssuré: beneficiary.numeroAssuré || beneficiary.numben || beneficiary.id,
                    isValid: true,
                    fullData: beneficiary
                }));
            } else if (response.data && Array.isArray(response.data)) {
                return response.data.map(beneficiary => ({
                    id: beneficiary.id || beneficiary.idClient,
                    name: beneficiary.lastName || beneficiary.nom || 'Nom non disponible',
                    firstName: beneficiary.firstName || beneficiary.prenom || 'Prénom non disponible',
                    policeNumber: beneficiary.policeNumber || beneficiary.numeroPolice || policeNumber,
                    familyId: beneficiary.familyId || beneficiary.numeroFamille || 'N/A',
                    numeroAssuré: beneficiary.numeroAssuré || beneficiary.numben || beneficiary.id,
                    isValid: true,
                    fullData: beneficiary
                }));
            }

            return [];
        } catch (error) {
            console.error('Erreur lors de la recherche par police:', error);

            // Fallback: essayer avec l'endpoint Client
            try {
                const fallbackResponse = await axios.get(`${this.clientURL}/search`, {
                    params: {
                        police: policeNumber
                    }
                });

                if (fallbackResponse.data) {
                    const clients = Array.isArray(fallbackResponse.data) ? fallbackResponse.data : [fallbackResponse.data];
                    return clients.map(client => ({
                        id: client.idClient || client.id,
                        name: client.nom || client.lastName || 'Nom non disponible',
                        firstName: client.prenom || client.firstName || 'Prénom non disponible',
                        policeNumber: policeNumber,
                        familyId: client.numeroFamille || 'N/A',
                        numeroAssuré: client.numben || client.numeroAssuré || client.id,
                        isValid: true,
                        fullData: client
                    }));
                }
            } catch (fallbackError) {
                console.warn('Fallback search aussi échoué:', fallbackError.message);
            }

            throw error;
        }
    }

    /**
     * Recherche des bénéficiaires par numéro de famille
     */
    async searchByFamilyNumber(familyNumber) {
        try {
            // Utiliser l'endpoint de liste avec filtre sur la famille
            const response = await axios.get(`${this.baseURL}/list`, {
                params: {
                    famille: familyNumber,
                    page: 1,
                    pageSize: 50
                }
            });

            if (response.data && response.data.data) {
                return response.data.data.map(beneficiary => ({
                    id: beneficiary.id || beneficiary.idClient,
                    name: beneficiary.lastName || beneficiary.nom || 'Nom non disponible',
                    firstName: beneficiary.firstName || beneficiary.prenom || 'Prénom non disponible',
                    policeNumber: beneficiary.policeNumber || beneficiary.numeroPolice || 'N/A',
                    familyId: beneficiary.familyId || beneficiary.numeroFamille || familyNumber,
                    numeroAssuré: beneficiary.numeroAssuré || beneficiary.numben || beneficiary.id,
                    isValid: true,
                    fullData: beneficiary
                }));
            }

            return [];
        } catch (error) {
            console.error('Erreur lors de la recherche par famille:', error);
            throw error;
        }
    }

    /**
     * Validation d'un bénéficiaire spécifique
     */
    async validateBeneficiary(beneficiaryId) {
        try {
            const response = await axios.get(`${this.baseURL}/${beneficiaryId}`);
            return {
                isValid: true,
                data: response.data,
                restrictions: []
            };
        } catch (error) {
            if (error.response?.status === 404) {
                return {
                    isValid: false,
                    error: 'Bénéficiaire non trouvé',
                    restrictions: []
                };
            }
            throw error;
        }
    }

    /**
     * Vérification des restrictions pour un bénéficiaire
     */
    async checkBeneficiaryRestrictions(beneficiaryId) {
        try {
            const response = await axios.get(`${this.baseURL}/${beneficiaryId}/restrictions`);
            return response.data || [];
        } catch (error) {
            console.warn(`Impossible de vérifier les restrictions pour ${beneficiaryId}:`, error.message);
            return [];
        }
    }

    /**
     * Recherche générale par mot-clé
     */
    async searchByKeyword(keyword) {
        try {
            const response = await axios.get(`${this.clientURL}/search/${encodeURIComponent(keyword)}`);

            if (response.data) {
                const clients = Array.isArray(response.data) ? response.data : [response.data];
                return clients.map(client => ({
                    id: client.idClient || client.id,
                    name: client.nom || client.lastName || 'Nom non disponible',
                    firstName: client.prenom || client.firstName || 'Prénom non disponible',
                    policeNumber: client.numeroPolice || 'N/A',
                    familyId: client.numeroFamille || 'N/A',
                    numeroAssuré: client.numben || client.numeroAssuré || client.id,
                    isValid: true,
                    fullData: client
                }));
            }

            return [];
        } catch (error) {
            console.error('Erreur lors de la recherche par mot-clé:', error);
            throw error;
        }
    }

    /**
     * Formatage des erreurs
     */
    handleError(error) {
        if (error.response) {
            const { status, data } = error.response;
            switch (status) {
                case 400:
                    return new Error(data.message || 'Données de recherche invalides');
                case 401:
                    return new Error('Non autorisé - Veuillez vous connecter');
                case 403:
                    return new Error('Accès interdit');
                case 404:
                    return new Error('Aucun bénéficiaire trouvé');
                case 500:
                    return new Error(data.message || 'Erreur serveur lors de la recherche');
                default:
                    return new Error(data.message || `Erreur HTTP ${status}`);
            }
        } else if (error.request) {
            return new Error('Serveur non accessible - Vérifiez votre connexion');
        } else {
            return new Error(error.message || 'Erreur lors de la recherche');
        }
    }
}

// Instance unique du service
export const beneficiarySearchService = new BeneficiarySearchService();
export default BeneficiarySearchService;